//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

//Local libs
import Service from 'src/app/common/service';
import SessionModel from './session.model';

/**
 * @service SessionService
 * @description Service for managing user authentication and session lifecycles.
 * This service handles the core security operations of the application, including logging in,
 * logging out, and maintaining session state via tokens.
 *
 * It interacts with the `/session/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Authentication**: Verifying user credentials and establishing a session.
 * 2. **Token Management**: Storing and managing JWT access and refresh tokens in `localStorage`.
 * 3. **Session Maintenance**: Updating session details, such as device tokens for push notifications.
 * 4. **Termination**: Securely ending sessions on the server side.
 *
 * **Data Flow:**
 * - **Login**: User submits credentials -> Service posts to `/session/login` -> Backend returns tokens -> Service saves to localStorage.
 * - **Logout**: User initiates logout -> Service posts to `/session/logout` -> Backend invalidates session.
 *
 * @feature 1. User Login with credential validation.
 * @feature 2. User Logout and session termination.
 * @feature 3. Session updates (e.g., Device Token registration).
 *
 * @architecture
 * Extends base Service<SessionModel> providing REST API integration.
 * Uses HttpClient for HTTP operations.
 * Directly manipulates `localStorage` for token persistence.
 *
 * @service-endpoint /session/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - token: The JWT access token used for authorizing subsequent API requests.
 * - refreshtoken: The token used to refresh the access token when it expires.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService extends Service<SessionModel> {
  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/session/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/session/');
  }

  /**
   * @method sessionLogin
   * @async
   * @description Authenticate a user and establish a new session.
   *
   * Sends credentials to the backend. Upon success, it extracts the `token` and `refreshtoken`
   * from the response and stores them in the browser's `localStorage`.
   *
   * @param {string} username - The user's login identifier (converted to lowercase).
   * @param {string} password - The user's password.
   * @param {string} appName - The name of the application context (e.g., 'AquaCloud Web').
   * @returns {Promise<SessionModel>} Promise resolving to the session details including user info and tokens.
   * @throws {string} Rejects with the error message if authentication fails.
   * @side-effect
   * - Initiates an HTTP POST request to `login`.
   * - Writes `token` to `localStorage`.
   * - Writes `refreshtoken` to `localStorage`.
   *
   * @example
   * sessionService.sessionLogin('user@example.com', 'secret', 'Web').then(session => {
   *   console.log('Logged in as:', session.user);
   * }).catch(err => {
   *   console.error('Login failed:', err);
   * });
   */
  public sessionLogin(username: string, password: string, appName: string) {
    return new Promise<SessionModel>((resolve, reject) => {
      this.httpClient.post<SessionModel>(`${this.baseUrl}${this.serviceUrl}login`, {
        username: username.toLowerCase(),
        password: password,
        appName: appName
      }, this.httpOptions)
        .subscribe(response => {
          localStorage.setItem('token', response.tokens.token);
          localStorage.setItem('refreshtoken', response.tokens.refreshtoken);
          resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
    });
  }

  /**
   * @method sessionLogout
   * @async
   * @description Terminate an active user session.
   *
   * Sends a request to the backend to invalidate the session associated with the provided User ID.
   * Note: This method does not automatically clear localStorage; that is typically handled by the component calling this method.
   *
   * @param {string} userID - The unique identifier of the user logging out.
   * @returns {Promise<SessionModel>} Promise resolving to the operation result.
   * @throws {string} Rejects with the error message if the request fails.
   * @side-effect Initiates an HTTP POST request to `logout`.
   */
  public sessionLogout(userID: string) {
    return new Promise<SessionModel>((resolve, reject) => {
      this.httpClient.post<SessionModel>(`${this.baseUrl}${this.serviceUrl}logout`, {
        userID: userID
      }, this.httpOptions)
        .subscribe(response => {
          resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
    });
  }

  /**
   * @method sessionUpdate
   * @async
   * @description Update session details, specifically the Device Token (DTN).
   *
   * This is often used to register a device for push notifications after a successful login
   * or when the token is refreshed by the messaging service.
   *
   * @param {string} userId - The unique identifier of the user.
   * @param {string} deviceToken - The new device token string.
   * @returns {Promise<SessionModel>} Promise resolving to the updated session model.
   * @throws {string} Rejects with the error message if the request fails.
   * @side-effect Initiates an HTTP PUT request to `dtn`.
   */
  public sessionUpdate(userId: string, deviceToken: string): Promise<SessionModel> {
    return new Promise<SessionModel>((resolve, reject) => {
      this.httpClient.put<SessionModel>(`${this.baseUrl}${this.serviceUrl}dtn`, {
        userID: userId,
        dtn: deviceToken
      }, this.httpOptions)
        .subscribe(response => {
          resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
    });
  }
}
